import React, { useEffect, useState } from 'react';
import RosterModal from './RosterModal';
import DraftOrderModal from './DraftOrderModal';

import './DraftLobby.css';

import api from '../utils/api';
import Loader from '../lib/components/Loader';
import OverlayLoader from '../lib/components/OverlayLoader';
import moment from 'moment';
import viewUtil from '../lib/viewUtil';
import constants from '../lib/constants';

const { managerList } = constants;

const DRAFT_YEAR = 2026;
const DRAFT_PAUSED = false;

const getRoundAndPick = (pickNumber) => ({ round: Math.ceil(pickNumber / 12), pick: pickNumber % 12 || 12 });

const getCardId = (index) => `draft-pick-card-${index}`;

const formatDraftCard = (time) => {
    const day = moment.utc(time).dayOfYear();
    return `${day > 1 ? `${day - 1}:` : ''}${moment.utc(time).format('HH:mm:ss')}`;
}

const DraftCardTimeElapsed = ({ lastTimestamp }) => {
    const [timeSinceLastPick, setTimeSinceLastPick] = useState(null);

    setTimeout(() => {
        const timeDifference = moment.utc().diff(lastTimestamp);
        setTimeSinceLastPick(timeDifference);
    }, 1000);

    return <div><i className="bi bi-clock-history"/> {timeSinceLastPick ? formatDraftCard(timeSinceLastPick) : '--'}</div>;
};

const DraftSummary = ({ draftPicks, permissions, managerOnTheClockIndex }) => {

    useEffect(() => {
        const screenWidth = window.innerWidth;
        const totalDraftPicks = draftPicks.length;
        const numToDivideBy = screenWidth > 900 ? 200 : 160; // 200 is arbitrary, 160 is the width of the cards
        const numCardsInView = Math.floor(screenWidth / numToDivideBy);
        const cardIdToScrollTo = managerOnTheClockIndex > totalDraftPicks - numCardsInView ? totalDraftPicks - 1 : managerOnTheClockIndex + numCardsInView - 1;
        const card = document.getElementById(getCardId(cardIdToScrollTo));

        if (card) {
            card.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [])
    
    return (
        <div>
            <div id="draft-summary" style={{overflowX: 'scroll', whiteSpace: 'nowrap', textAlign: 'center'}}>
                {draftPicks.map((draftPick, index) => {
                    const millisElapsed = index === 0 || index > managerOnTheClockIndex ? 0 : moment.utc(draftPicks[index].timestamp).diff(draftPicks[index - 1].timestamp);
                    
                    return (
                        <div
                            id={getCardId(index)}
                            className={`card d-inline-block ${managerOnTheClockIndex === index ? 'bg-light text-dark glowBorder' : permissions === draftPick.manager ? 'bg-success text-white whiteBorder' : 'bg-dark text-white border-secondary'}`}
                            style={{ width: '10rem', margin: '2px' }}
                            key={`draft-pick-card${index}`
                        }>
                            <div 
                                className="card-header d-flex flex-row justify-content-between" 
                                style={{ backgroundColor: 'inherit', padding: '0 4px'} }
                            >
                                <div style={{ fontSize: '10px' }}>
                                    R{getRoundAndPick(index + 1).round} P{index + 1}
                                </div>
                                <div className={`${managerOnTheClockIndex === index ? 'fs-2' : 'fs-5'}`}>
                                    <strong>{draftPick.manager.toUpperCase()}</strong>
                                </div>
                            </div>
                            <div className="card-body">
                                <div>
                                    {draftPick.pick || '--'}
                                </div>
                                {
                                    index === managerOnTheClockIndex && index !== 0 ? 
                                        <DraftCardTimeElapsed timeSinceLastPick={millisElapsed} lastTimestamp={draftPicks[managerOnTheClockIndex - 1].timestamp}/>: 
                                        <div style={{ fontSize: '10px' }}><i className="bi bi-clock-history"/> {millisElapsed <= 0 ? '--' : formatDraftCard(millisElapsed)}</div>
                                }
                            </div>
                        </div>
                    )
                })}

            </div>
        </div>
    )
}

const PreviousPick = ({ manager, pick }) => {
    return (
        <div>
            <span>Last Pick: <em>{manager}</em> - <strong>{pick}</strong></span>
        </div>
    )
}

const OnTheClock = ({ managerOnTheClockIndex, managerOnTheClock, draftPicks, permissions }) => {
    const { round, pick } = getRoundAndPick(managerOnTheClockIndex + 1);

    const managerNextPick = draftPicks.slice(managerOnTheClockIndex).findIndex(pick => pick.manager === permissions);
    const nextPickText = managerNextPick === -1 ? 'No more picks remaining' : `Your next pick is in ${managerNextPick} picks`;

    return (
        <div>
            <div className="d-flex flex-row justify-content-between">
                <div>Round {round} Pick {pick}</div>
                <div>
                    {managerOnTheClockIndex === 0 ? <div>First pick of the draft</div> : 
                        <PreviousPick manager={draftPicks[managerOnTheClockIndex - 1].manager} pick={draftPicks[managerOnTheClockIndex - 1].pick}/>}
                    { permissions === managerOnTheClock ?
                        <div className="text-success">You are on the clock!</div> :
                        <div className="text-secondary">{nextPickText}</div>
                    }
                </div>
            </div>
            <div className="text-center">
                <div className="fs-1">On the clock: {managerOnTheClock} (R{round} P{pick})</div>
            </div>
            {/* <div>Time since last pick: {timeSinceLastPick ? moment.utc(timeSinceLastPick).format('HH:mm:ss') : '--'}</div> */}
        </div>
    )
}

const DraftBoard = ({ draftablePlayers, canMakeSelection, handleSelectPlayerId, handleSelectPlayerByName }) => {
    const activeDraftablePlayers = draftablePlayers.filter(player => player.active !== false);
    const [filteredDraftablePlayers, setFilteredDraftablePlayers] = useState(activeDraftablePlayers);
    const [filterQB, setFilterQB] = useState(false);
    const [filterRB, setFilterRB] = useState(false);
    const [filterWR, setFilterWR] = useState(false);
    const [filterTE, setFilterTE] = useState(false);
    const [filterDEF, setFilterDEF] = useState(false);
    const [filterK, setFilterK] = useState(false);
    
    const [selectedPlayerIndex, setSelectedPlayerIndex] = useState(null);
    const [selectPlayerInput, setSelectPlayerInput] = useState('');
    
    const isPlayerSelected = typeof selectedPlayerIndex === 'number';

    useEffect(() => {
        setFilteredDraftablePlayers(activeDraftablePlayers);
    }, [draftablePlayers])

    const filterPlayers = () => {
        let filteredPlayers = activeDraftablePlayers;
        setSelectedPlayerIndex(null);
        if (filterQB || filterRB || filterWR || filterTE || filterDEF || filterK) {
            const posFilters = {
                QB: filterQB,
                RB: filterRB,
                WR: filterWR,
                TE: filterTE,
                DEF: filterDEF,
                K: filterK
            };
            filteredPlayers = filteredPlayers.filter(player => posFilters[player.posname]);
        }
        if (selectPlayerInput) {
            filteredPlayers = filteredPlayers.filter(player => player.name.toLowerCase().includes(selectPlayerInput));
        }
        setFilteredDraftablePlayers(filteredPlayers);
    }

    useEffect(filterPlayers, [selectPlayerInput, filterQB, filterRB, filterWR, filterTE, filterDEF, filterK])

    const handlePlayerSearch = (e) => {
        const searchCriteria = e.currentTarget.value?.toLowerCase();
        setSelectPlayerInput(searchCriteria);
    };

    const handleSelectPlayer = ({ playerAction, playerArg }) => {
        playerAction(playerArg);
        setSelectPlayerInput('');
        setSelectedPlayerIndex(null);
    };

    return (
        <div>
            {canMakeSelection ? 
                <div className="text-center">
                    {isPlayerSelected ? <div className="fs-2">SELECTING: {filteredDraftablePlayers[selectedPlayerIndex].name}</div> : ''}
                    <button 
                        className="btn btn-large btn-success fs-1"
                        disabled={!isPlayerSelected || !canMakeSelection}
                        onClick={() => handleSelectPlayer({ playerAction: handleSelectPlayerId, playerArg: filteredDraftablePlayers[selectedPlayerIndex].id })}
                    >
                        MAKE YOUR PICK
                    </button>
                </div>
                :
                ''
            }
            <h4>Filters</h4>
            <div className="d-flex flex-row justify-content-between align-items-center overflow-scroll mb-2">
                <div style={{ minWidth: '120px', marginRight: '8px'}}>
                    {/* <label className="form-label">Search for a player</label> */}
                    <input
                        id="draft-board-player-search"
                        className="form-control bg-dark text-white border-secondary"
                        type="text"
                        placeholder="Player Name"
                        value={selectPlayerInput}
                        onChange={handlePlayerSearch}
                    />
                </div>
                <div className="d-flex flex-row">
                    <div className="form-check filterCheckbox">
                        <label className="form-check-label">QB</label>
                        <input className="form-check-input" type="checkbox" checked={filterQB} onChange={() => setFilterQB(!filterQB)}/>
                    </div>
                    <div className="form-check filterCheckbox">
                        <label className="form-check-label">RB</label>
                        <input className="form-check-input" type="checkbox" checked={filterRB} onChange={() => setFilterRB(!filterRB)}/>
                    </div>
                    <div className="form-check filterCheckbox">
                        <label className="form-check-label">WR</label>
                        <input className="form-check-input" type="checkbox" checked={filterWR} onChange={() => setFilterWR(!filterWR)}/>
                    </div>
                    <div className="form-check filterCheckbox">
                        <label className="form-check-label">TE</label>
                        <input className="form-check-input" type="checkbox" checked={filterTE} onChange={() => setFilterTE(!filterTE)}/>
                    </div>
                    <div className="form-check filterCheckbox">
                        <label className="form-check-label">DEF</label>
                        <input className="form-check-input" type="checkbox" checked={filterDEF} onChange={() => setFilterDEF(!filterDEF)}/>
                    </div>
                    <div className="form-check filterCheckbox">
                        <label className="form-check-label">K</label>
                        <input className="form-check-input" type="checkbox" checked={filterK} onChange={() => setFilterK(!filterK)}/>
                    </div>
                </div>
            </div>
            <div className="table-responsive">
                <table className="table table-dark table-striped table-hover">
                    <thead>
                        <tr>
                            <th scope="col">#</th>
                            <th scope="col">Player</th>
                            <th scope="col">Team</th>
                            <th scope="col">Position</th>
                            <th scope="col">Age</th>
                            <th scope="col">Y!</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredDraftablePlayers.map((player, index) => {
                            return (
                                <tr onClick={() => setSelectedPlayerIndex(index)}
                                    key={`draft-board-row-${index}`}
                                    className={selectedPlayerIndex === index ? 'table-success' : ''}
                                    style={{cursor: 'pointer'}}>
                                    <td>{index + 1}</td>
                                    <td>{player.name}</td>
                                    <td>{player.nflteam}</td>
                                    <td>{player.posname}</td>
                                    <td>{viewUtil.getAge(player.birthdate) ?? '--'}</td>
                                    <td>{player.yahooleaguerank || '--'}</td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
            <form className="d-flex flex-column form">
                <div>Not seeing your player?</div>
                <input className="form-control bg-dark text-white border-secondary mb-1" value={selectPlayerInput} type="text" placeholder="Player name" onChange={(e) => setSelectPlayerInput(e.currentTarget.value)}/>
                <button
                    className="btn btn-success"
                    disabled={!canMakeSelection || !selectPlayerInput}
                    onClick={() => handleSelectPlayer({ playerAction: handleSelectPlayerByName, playerArg: selectPlayerInput })}
                >SUBMIT PLAYER</button>
            </form>
        </div>
    )
}

const CompletedDraftByManager = ({ picks }) => {
    const numberedPicks = picks.map((pick, index) => ({ ...pick, pickNumber: index + 1 }));

    return (
        <div className="row">
            {managerList.map(manager => {
                const managerPicks = numberedPicks.filter(pick => pick.manager === manager);

                return (
                    <div className="col-lg-6 col-xl-4 mb-3" key={`manager-picks-${manager}`}>
                        <div className="card bg-dark text-white mb-3">
                            <div className="card-header">{manager} ({managerPicks.length} picks)</div>
                            <div className="table-responsive">
                                <table className="table table-dark table-striped table-hover table-sm mb-0">
                                    <thead>
                                        <tr>
                                            <th>Round</th>
                                            <th>Player</th>
                                            <th>Time of pick</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {managerPicks.map(pick => (
                                            <tr key={`manager-pick-row-${manager}-${pick.pickNumber}`}>
                                                <td>{getRoundAndPick(pick.pickNumber).round}</td>
                                                <td>{pick.pick}</td>
                                                <td>{moment(pick.timestamp).format('hh:mm:ss a MM-DD-YYYY')}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const DraftLobby = ({ userData, playerData, fetchPlayerData }) => {
    const [draftData, setDraftData] = useState(null);
    const [draftBoard, setDraftBoard] = useState(null);
    const [draftStats, setDraftStats] = useState(null);
    const [draftDataError, setDraftDataError] = useState(false);
    const [showOverlayLoader, setShowOverlayLoader] = useState(false);
    const [managerOnTheClockIndex, setManagerOnTheClockIndex] = useState(null);
    const [completedDraftView, setCompletedDraftView] = useState('pick');

    const fetchDraftData = () => {
        api.get(`/api/v1/draft?draftYear=${DRAFT_YEAR}`).then(({ data }) => {
            setManagerOnTheClockIndex(data.picks.findIndex(pick => !pick.pick));
            setDraftData(data);
        }).catch(res => {
            console.error('Error retrieving draft data: ' + res);
            setDraftDataError(true);
        });
    };
    
    const fetchDraftBoard = () => {
        api.get(`/api/v1/draft/${DRAFT_YEAR}/available`).then(({ data }) => {
            setDraftBoard(data);
        }).catch(res => {
            console.error('Error retrieving draft board data: ' + res);
            setDraftDataError(true);
        });
    };

    const fetchDraftStats = () => {
        api.get(`/api/v1/draft/${DRAFT_YEAR}/stats`).then(({ data }) => {
            setDraftStats(data);
        }).catch(res => {
            console.error('Error retrieving draft stats: ' + res);
            setDraftStats({});
        });
    }

    useEffect(() => {
        fetchDraftData();
        fetchDraftBoard();
        fetchDraftStats();
        // const refreshInterval = setInterval(() => {
        //     api.get(`/api/v1/draft?draftYear=${DRAFT_YEAR}`).then(({ data: fetchedDraftData }) => {
        //         setManagerOnTheClockIndex(fetchedDraftData.picks.findIndex(pick => !pick.pick));
        //         setDraftData(fetchedDraftData);
        //         // if (fetchedDraftData.picks.findIndex(pick => !pick.pick) !== getManagerOnTheClockIndex()) {
        //         //     api.get(`/api/v1/draft/${DRAFT_YEAR}/available`).then(({ data: draftBoardData }) => {
        //         //         setManagerOnTheClockIndex(fetchedDraftData.picks.findIndex(pick => !pick.pick));
        //         //         setDraftBoard(draftBoardData);
        //         //     });
        //         // }
        //     }).catch(res => {
        //         console.error('Error retrieving draft data: ' + res);
        //     });
        //     api.get(`/api/v1/draft/${DRAFT_YEAR}/available`).then(({ data: draftBoardData }) => {
        //         setDraftBoard(draftBoardData);
        //     });
        // }, 8000);

        // return () => { clearInterval(refreshInterval) };
    }, []);

    if (managerOnTheClockIndex === -1) {
        return draftDataError ? <div>Error retrieving draft data</div> : draftData && draftBoard ? (
            <>
                <h2>{DRAFT_YEAR} Draft Complete!</h2>
                <ul className="nav nav-tabs nav-tabs-dark mb-3">
                    <li className="nav-item">
                        <button className={`nav-link ${completedDraftView === 'pick' ? 'active' : ''}`} onClick={() => setCompletedDraftView('pick')}>By Pick</button>
                    </li>
                    <li className="nav-item">
                        <button className={`nav-link ${completedDraftView === 'manager' ? 'active' : ''}`} onClick={() => setCompletedDraftView('manager')}>By Manager</button>
                    </li>
                </ul>
                {completedDraftView === 'pick' ? (
                    <div className="table-responsive">
                        <table className="table table-dark table-striped table-hover">
                            <thead>
                                <tr>
                                    <th>Pick</th>
                                    <th>Manager</th>
                                    <th>Player</th>
                                    <th>Time of pick</th>
                                </tr>
                            </thead>
                            <tbody>
                                {draftData.picks.map((pick, index) => (
                                    <tr key={`draft-complete-row-${index}`}>
                                        <td>{index + 1}</td>
                                        <td>{pick.manager}</td>
                                        <td>{pick.pick}</td>
                                        <td>{moment(pick.timestamp).format('hh:mm:ss a MM-DD-YYYY')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <CompletedDraftByManager picks={draftData.picks}/>
                )}
            </>
        ) : <Loader/>
    }

    const managerOnTheClock = draftData?.picks[managerOnTheClockIndex]?.manager;
    const canMakeSelection = !DRAFT_PAUSED && (userData.permissions === 'admin' || userData.permissions === managerOnTheClock);
    
    const handleDraftSelectionByPlayerId = (playerId) => {
        setShowOverlayLoader(true);
        api.post(`api/v1/draft/${DRAFT_YEAR}/selection`, {
            pickNumber: managerOnTheClockIndex + 1,
            playerId
        })
            .then(() => {
                alert('Pick made successfully!');
                fetchDraftData();
                fetchDraftBoard();
                fetchDraftStats();
            })
            .catch(() => alert('Error making selection'))
            .finally(() => setShowOverlayLoader(false));
    };
    
    const handleDraftSelectionByPlayerName = (playerName) => {
        setShowOverlayLoader(true);
        api.post(`api/v1/draft/${DRAFT_YEAR}/selection`, {
            pickNumber: managerOnTheClockIndex + 1,
            playerName
        })
            .then(() => {
                alert('Pick made successfully!');
                fetchDraftData();
                fetchDraftBoard();
                fetchDraftStats();
            })
            .catch(() => alert('Error making selection'))
            .finally(() => setShowOverlayLoader(false));
    };

    return (
        <div>
            {showOverlayLoader && <OverlayLoader/>}
            {DRAFT_PAUSED && (
                <div className="alert alert-warning d-flex align-items-center gap-2 mb-3" role="alert">
                    <i className="bi bi-lock-fill fs-5"/>
                    <div>
                        <strong>Picks are not open yet.</strong> You can browse the draft board and pick order, but selections are disabled until the draft begins.
                    </div>
                </div>
            )}
            {draftDataError ? <div>Error retrieving draft data</div> : draftData && draftBoard ?
                <>
                    <DraftSummary
                        draftPicks={draftData.picks} 
                        permissions={userData.permissions}
                        managerOnTheClockIndex={managerOnTheClockIndex}
                    /> 
                    <OnTheClock 
                        managerOnTheClock={managerOnTheClock} 
                        managerOnTheClockIndex={managerOnTheClockIndex} 
                        draftPicks={draftData.picks}
                        permissions={userData.permissions}
                    />
                    <div className="d-flex gap-2 flex-column flex-lg-row justify-content-around">
                        <div>
                            <div className="d-flex justify-content-between mb-2">
                                <button type="button" className="btn btn-secondary" data-bs-toggle="modal" data-bs-target="#draft-order-modal-container">
                                    <i className="bi bi-list-ol me-1"></i> Draft Order &amp; Projections
                                </button>
                                <button type="button" className="btn btn-info" data-bs-toggle="modal" data-bs-target="#roster-modal-container">
                                    Rosters <i className="bi bi-caret-right-fill"></i><i className="bi bi-caret-right-fill"></i>
                                </button>
                            </div>
                            <DraftBoard
                                draftablePlayers={draftBoard}
                                canMakeSelection={canMakeSelection}
                                handleSelectPlayerId={handleDraftSelectionByPlayerId}
                                handleSelectPlayerByName={handleDraftSelectionByPlayerName}
                            />
                        </div>
                        <div id="roster-modal-container" className="modal fade">
                            <div className="modal-dialog modal-lg">
                                <div className="modal-content">
                                    <div className="modal-header">
                                        <h5 className="modal-title">Rosters</h5>
                                        <button type="button" className="btn" data-bs-dismiss="modal"><i className="bi bi-x fs-2"></i></button>
                                    </div>
                                    <div className="modal-body">
                                        <RosterModal
                                            players={playerData}
                                            draftPicks={draftData.picks}
                                            permissions={userData.permissions}
                                            draftStats={draftStats?.managerData}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <DraftOrderModal
                            draftPicks={draftData.picks}
                            draftBoard={draftBoard}
                            playerData={playerData}
                            managerOnTheClockIndex={managerOnTheClockIndex}
                        />
                    </div>
                </>
                : 
                <Loader/>
            }
        </div>
    );
}

export default DraftLobby;